import React from 'react';
import { Box, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { vortxTypography } from '@vortx/shared-ui';
import { COLORS } from '../results/ResultsDesignTokens';

const accentColor = '#9b59b6';

// Custom renderers for retro-styled markdown
const components = {
    h1: ({ children }) => (
        <Typography sx={{
            fontFamily: vortxTypography.heading,
            fontSize: '1.6rem',
            fontWeight: 700,
            color: COLORS.text.primary,
            mb: 2,
            mt: 1,
            textShadow: `0 0 20px ${accentColor}40`,
        }}>
            {children}
        </Typography>
    ),
    h2: ({ children }) => (
        <Typography sx={{
            fontFamily: vortxTypography.heading,
            fontSize: '1.15rem',
            fontWeight: 600,
            color: accentColor,
            mb: 1.5,
            mt: 3,
            pb: 0.5,
            borderBottom: `1px solid ${accentColor}30`,
            textShadow: `0 0 12px ${accentColor}30`,
        }}>
            {children}
        </Typography>
    ),
    h3: ({ children }) => (
        <Typography sx={{
            fontFamily: vortxTypography.heading,
            fontSize: '0.95rem',
            fontWeight: 600,
            color: COLORS.text.primary,
            mb: 1,
            mt: 2,
        }}>
            {children}
        </Typography>
    ),
    p: ({ children }) => (
        <Typography sx={{
            fontFamily: vortxTypography.body,
            fontSize: '0.85rem',
            lineHeight: 1.7,
            color: COLORS.text.primary,
            mb: 1.5,
        }}>
            {children}
        </Typography>
    ),
    a: ({ href, children }) => (
        <Box component="a" href={href} sx={{
            color: accentColor,
            textDecoration: 'none',
            borderBottom: `1px solid ${accentColor}40`,
            transition: 'all 0.2s ease',
            '&:hover': {
                borderBottomColor: accentColor,
                textShadow: `0 0 8px ${accentColor}60`,
            },
        }}>
            {children}
        </Box>
    ),
    strong: ({ children }) => (
        <Box component="strong" sx={{
            fontWeight: 700,
            color: COLORS.text.primary,
        }}>
            {children}
        </Box>
    ),
    code: ({ inline, className, children }) => {
        if (inline) {
            return (
                <Box component="code" sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.8rem',
                    backgroundColor: 'rgba(155, 89, 182, 0.15)',
                    color: accentColor,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: '3px',
                    border: `1px solid ${accentColor}20`,
                }}>
                    {children}
                </Box>
            );
        }
        return (
            <Box component="pre" sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.78rem',
                lineHeight: 1.6,
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                border: `1px solid ${accentColor}20`,
                borderRadius: '4px',
                p: 2,
                mb: 2,
                overflowX: 'auto',
                color: COLORS.text.primary,
                '& code': {
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                },
            }}>
                <code>{children}</code>
            </Box>
        );
    },
    ul: ({ children }) => (
        <Box component="ul" sx={{
            fontFamily: vortxTypography.body,
            fontSize: '0.85rem',
            lineHeight: 1.7,
            color: COLORS.text.primary,
            mb: 1.5,
            pl: 2.5,
            '& li': { mb: 0.5 },
            '& li::marker': { color: accentColor },
        }}>
            {children}
        </Box>
    ),
    ol: ({ children }) => (
        <Box component="ol" sx={{
            fontFamily: vortxTypography.body,
            fontSize: '0.85rem',
            lineHeight: 1.7,
            color: COLORS.text.primary,
            mb: 1.5,
            pl: 2.5,
            '& li': { mb: 0.5 },
            '& li::marker': { color: accentColor },
        }}>
            {children}
        </Box>
    ),
    table: ({ children }) => (
        <Box sx={{
            overflowX: 'auto',
            mb: 2,
            border: `1px solid ${accentColor}20`,
            borderRadius: '4px',
        }}>
            <Box component="table" sx={{
                width: '100%',
                borderCollapse: 'collapse',
                fontFamily: vortxTypography.mono,
                fontSize: '0.78rem',
            }}>
                {children}
            </Box>
        </Box>
    ),
    thead: ({ children }) => (
        <Box component="thead" sx={{
            backgroundColor: `${accentColor}15`,
            '& th': {
                fontFamily: vortxTypography.mono,
                fontSize: '0.75rem',
                fontWeight: 600,
                color: accentColor,
                textAlign: 'left',
                px: 1.5,
                py: 1,
                borderBottom: `1px solid ${accentColor}30`,
            },
        }}>
            {children}
        </Box>
    ),
    tbody: ({ children }) => (
        <Box component="tbody" sx={{
            '& td': {
                fontFamily: vortxTypography.mono,
                fontSize: '0.78rem',
                color: COLORS.text.primary,
                px: 1.5,
                py: 0.75,
                borderBottom: `1px solid ${accentColor}10`,
            },
            '& tr:hover': {
                backgroundColor: `${accentColor}08`,
            },
        }}>
            {children}
        </Box>
    ),
    blockquote: ({ children }) => (
        <Box sx={{
            borderLeft: `3px solid ${accentColor}`,
            pl: 2,
            ml: 0,
            mb: 1.5,
            py: 0.5,
            backgroundColor: `${accentColor}08`,
            borderRadius: '0 4px 4px 0',
            '& p': { mb: 0.5 },
        }}>
            {children}
        </Box>
    ),
    hr: () => (
        <Box
            component="hr"
            sx={{
                border: 'none',
                borderTop: `1px solid ${accentColor}20`,
                my: 3,
            }}
        />
    ),
};

const WikiContent = ({ markdown }) => {
    if (!markdown) {
        return (
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.85rem',
                color: COLORS.text.muted,
                textAlign: 'center',
                py: 8,
            }}>
                Select a page from the table of contents.
            </Typography>
        );
    }

    return (
        <Box sx={{ maxWidth: 800, px: 3, py: 2 }}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={components}
            >
                {markdown}
            </ReactMarkdown>
        </Box>
    );
};

export default WikiContent;
